import { CheckCircledIcon, FontBoldIcon, FontItalicIcon, Link2Icon, ListBulletIcon, UnderlineIcon, UpdateIcon } from '@radix-ui/react-icons';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import {
    createButton,
    createDropdown,
    Editor,
    EditorProvider,
    Toolbar,
} from 'react-simple-wysiwyg';
import '../App.css';
import { Button } from '../components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../components/ui/form';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from '../components/ui/use-toast';
import { AuthContext } from '../context/AuthContext';
import api from '../lib/api';
import DOMPurify from 'dompurify';
export const BtnBulletList = createButton(
    'Bullet list',
    <ListBulletIcon />,
    'insertUnorderedList',
);
export const BtnItalic = createButton('Italic', <FontItalicIcon />, 'italic');
export const BtnBold = createButton('Bold', <FontBoldIcon />, 'bold')
export const BtnUnderline = createButton('Underline', <UnderlineIcon />, 'underline')
export const BtnLink = createButton('Link', <Link2Icon />, ({ $selection }) => {
    if ($selection?.nodeName === 'A') {
        document.execCommand('unlink');
    } else {
        // eslint-disable-next-line no-alert
        document.execCommand('createLink', false, prompt('URL', '') || undefined);
    }
});
export const BtnStyles = createDropdown('Stilar', [
    ['Normal', 'formatBlock', 'DIV'],
    ['𝐑𝐮𝐛𝐫𝐢𝐤 𝟏', 'formatBlock', 'H1'],
    ['Rubrik 2', 'formatBlock', 'H2'],
]);

function FarmInformation() {

    const { auth } = useContext(AuthContext);
    const form = useForm();
    const navigate = useNavigate();
    let { farmId } = useParams();

    farmId = parseInt(farmId);

    const [farm, setFarm] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [formData, setFormData] = useState({
        name: '',
    });
    const [description, setDescription] = useState('');

    const fetchfarmInformation = useCallback(() => {
        setIsLoading(true); // Start loading data

        api.get(`https://skhrf-api.cmdigital.se/v1/farms/information?farmId=${farmId}`)
            .then(response => {
                const farmData = response.data;
                setFarm(farmData);

                setFormData({
                    name: farmData.name || '',
                });
                const sanitizedDescription = DOMPurify.sanitize(farmData.description, {
                    ALLOWED_TAGS: ['div', 'br', 'b', 'i', 'u', 'a', 'h1', 'h2'],
                    ALLOWED_ATTR: ['href']
                });
                setDescription(sanitizedDescription || '');
                setIsLoading(false); // Data fetched, set loading to false
            })
            .catch(error => {
                console.error('Error fetching farm information:', error);
                setIsLoading(false); // Error occurred, set loading to false
            });
    }, [farmId]);

    const sendfarmInformation = (e) => {
        if (isLoading) return;
        e.preventDefault();
        setIsLoading(true);

        // Sanitize the HTML from the editor before sending
        const sanitizedDescription = DOMPurify.sanitize(description, {
            ALLOWED_TAGS: ['div', 'br', 'b', 'i', 'u', 'a', 'h1', 'h2'],
            ALLOWED_ATTR: ['href']
        });

        api.put(`https://skhrf-api.cmdigital.se/v1/farms/edit`, {
            farmId,
            description: sanitizedDescription,
            ...formData,
        }).then(response => {

            setIsLoading(false);
            fetchfarmInformation();
            toast({
                variant: 'success',
                title: 'Informationen uppdaterades!',
                description: "Informationen uppdaterades utan problem.",
            });
            navigate(`/farms/${farmId}`);
        }).catch(error => {
            toast({
                variant: 'destructive',
                title: 'Något gick fel.',
                description: error?.response?.data?.error,
            });
            setIsLoading(false);
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    useEffect(() => {
        fetchfarmInformation();
    }, [fetchfarmInformation]);

    return (
        <div className='flex w-full items-center justify-center'>
            <div className='flex w-5/6 sm:w-3/4 flex-col py-6 gap-3'>
                <h1 className='font-medium text-2xl mb-2'>
                    {isLoading ? (
                        <Skeleton className='w-64 h-8' />
                    ) : (
                        `Redigera ${farm.name}`
                    )}
                </h1>
                <Form {...form} >
                    <form className="flex flex-col gap-6 w-full sm:w-1/2">

                        <FormField
                            control={form.control}
                            name="name"
                            render={({ field }) => (
                                <FormItem className='space-y-0 grid gap-2'>
                                    <FormLabel>Namn</FormLabel>
                                    <FormControl>
                                        <Input placeholder="Johans gård" name="name" value={formData.name} onChange={handleChange} disabled={isLoading} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="description"
                            render={({ field }) => (
                                <FormItem className='space-y-0 grid gap-2'>
                                    <FormLabel>Gårdsbeskrivning</FormLabel>
                                    <FormControl>
                                        <EditorProvider>
                                            <Editor disabled={isLoading} value={description} onChange={(e) => setDescription(e.target.value)}>
                                                <Toolbar>
                                                    <BtnBold />
                                                    <BtnItalic />
                                                    <BtnUnderline />
                                                    <BtnBulletList />
                                                    <BtnLink />
                                                    <BtnStyles />
                                                </Toolbar>
                                            </Editor>
                                        </EditorProvider>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        {(farm.breeder_id === auth?.user?.id) && (
                            <p className='text-sm'>Vill du överlåta ägarskapet av din gård till någon annan? Endast kaninadministratörer har behörighet att göra detta. Kontakta UK för hjälp.</p>
                        )}
                        <Button type="submit" disabled={isLoading} onClick={sendfarmInformation}>
                            {isLoading ? <UpdateIcon className='loader-spinner' /> : <><CheckCircledIcon className='mr-3' />Uppdatera</>}
                        </Button>
                    </form>
                </Form>
            </div>
        </div>
    );
}

export default FarmInformation;
